import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import { remarkEditorMarks } from "./remark-editor-marks";
import { remarkFlattenForPreview } from "./remark-flatten-for-preview";
import { normalizeEntityCase } from "./decode-html-entities";

/**
 * Plain text for a Markdown field shown as a clamped one-or-two-line
 * preview next to rendered cards. Full-size surfaces render the same fields
 * with `MarkdownRender`.
 *
 * It parses with the dialect `MarkdownRender` renders (CommonMark + GFM + the
 * editor's `++`/`==` marks) and matches what `MarkdownRender inline` shows:
 * entities decode, ordered-list numbers stay, and raw HTML tags drop while
 * their text stays (`remarkFlattenForPreview`). Intraword `_` (`snake_case`)
 * and `a <5 & >18` survive (the regex stripper this replaced mangled both).
 */

interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
}

const processor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkEditorMarks)
  .use(remarkFlattenForPreview);

// Parents whose children are blocks: separate them with a space so list items
// and paragraphs don't run together once flattened.
const BLOCK_PARENTS = new Set(["root", "list", "listItem", "blockquote", "table", "tableRow"]);

function toText(node: MdNode): string {
  switch (node.type) {
    case "text":
    case "inlineCode":
    case "code":
      return node.value ?? "";
    case "break":
      return " ";
    case "image":
    case "imageReference":
      return "";
  }
  const parts = (node.children ?? []).map(toText);
  return parts.join(BLOCK_PARENTS.has(node.type) ? " " : "");
}

// Pure, so a cached result never goes stale. The landing showcase
// re-projects every loaded session on each new page, re-flattening the same
// course short descriptions; a `Map` keeps insertion order, so the oldest
// entry is evicted first.
const CACHE_LIMIT = 500;
const cache = new Map<string, string>();

export function markdownToPlainText(markdown: string | undefined | null): string {
  if (!markdown) return "";
  const cached = cache.get(markdown);
  if (cached !== undefined) return cached;

  const source = normalizeEntityCase(markdown);
  // The source goes in as the file so the plugin can read list markers.
  const tree = processor.runSync(processor.parse(source), source) as MdNode;
  const text = toText(tree).replace(/\s+/g, " ").trim();

  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value!);
  cache.set(markdown, text);
  return text;
}
